import { Chip, Tooltip } from '@mui/material';

/**
 * Renders the engine's current order-state-machine state for a row.
 *
 * The opener engine emits a specific state string (`maker_resting`,
 * `maker_adjusting`, `counter_taker_executing`, etc.) on every
 * transition; the gateway forwards it via `entry_status_update`. Until
 * this chip existed, the row had no visual surface for the state
 * machine — only price/size/threshold cells. Operators trying to
 * observe the cutover (Phase A) had to read backend logs to see
 * whether a maker was resting, mid-adjust, or hit the counter.
 *
 * The chip ALSO makes the 6 event types from `MISSING_STEPS.md` §5
 * (entry_maker_partial_fill, entry_partial_fill_accumulated,
 * entry_crossing_partial, entry_crossing_accumulated,
 * entry_recovery_filled, entry_recovery_restored) visible: each
 * corresponds to a specific state-machine state the engine routes
 * through, and that state shows here in real time. The notifications
 * page still owns the full event history; this chip is the
 * always-visible "right now" surface on the rule row.
 */

const STATE_DISPLAY: Record<
  string,
  { label: string; color: string; bg: string; tooltip: string }
> = {
  idle: {
    label: 'IDLE',
    color: '#cfd8dc',
    bg: 'rgba(96, 125, 139, 0.18)',
    tooltip:
      'Rule active; waiting for the orderbook to clear evaluation thresholds.',
  },
  maker_placing: {
    label: 'PLACING',
    color: '#ffb74d',
    bg: 'rgba(255, 152, 0, 0.18)',
    tooltip:
      'Submitting a new maker order to the exchange — wire call in flight.',
  },
  maker_resting: {
    label: 'RESTING',
    color: '#64b5f6',
    bg: 'rgba(33, 150, 243, 0.18)',
    tooltip:
      'Maker order resting on the exchange. GTC; will amend on size change or cancel on cap violation.',
  },
  maker_adjusting: {
    label: 'AMENDING',
    color: '#ba68c8',
    bg: 'rgba(156, 39, 176, 0.18)',
    tooltip:
      'Amending the resting maker (price or size moved). Falls back to cancel+replace if the exchange rejects the amend.',
  },
  maker_cancelling: {
    label: 'CANCELLING',
    color: '#ffcc80',
    bg: 'rgba(255, 167, 38, 0.18)',
    tooltip: 'Cancelling the resting maker.',
  },
  counter_checking: {
    label: 'COUNTER ?',
    color: '#aed581',
    bg: 'rgba(139, 195, 74, 0.18)',
    tooltip:
      'Maker filled — checking the counter platform orderbook for a placeable taker.',
  },
  counter_taker_executing: {
    label: 'COUNTER',
    color: '#81c784',
    bg: 'rgba(76, 175, 80, 0.22)',
    tooltip:
      'Hitting the counter platform with a taker order to close the entry pair.',
  },
  counter_maker_resting: {
    label: 'COUNTER M',
    color: '#80cbc4',
    bg: 'rgba(0, 150, 136, 0.18)',
    tooltip:
      'Counter leg placed as maker (no immediate liquidity). Will fill or be cancelled on rule pause.',
  },
  execution_complete: {
    label: 'COMPLETE',
    color: '#a5d6a7',
    bg: 'rgba(76, 175, 80, 0.32)',
    tooltip: 'Both legs filled. Rule cycle complete.',
  },
  error: {
    label: 'ERROR',
    color: '#ef9a9a',
    bg: 'rgba(244, 67, 54, 0.22)',
    tooltip:
      'Engine bailed on this rule. Check Slack and the notifications page for the underlying cause.',
  },
};

const FALLBACK = {
  label: '—',
  color: '#90a4ae',
  bg: 'rgba(96, 125, 139, 0.12)',
  tooltip: 'Unknown engine state.',
};

export function EngineStateChip({ state }: { state?: string | null }) {
  if (!state) return null;
  const cfg = STATE_DISPLAY[state] ?? {
    ...FALLBACK,
    label: state.toUpperCase().slice(0, 10),
    tooltip: `Unrecognized engine state: ${state}. The engine emits this string but the chip mapping has not been updated; report to the opener team.`,
  };
  return (
    <Tooltip title={cfg.tooltip} placement="top">
      <Chip
        label={cfg.label}
        size="small"
        sx={{
          fontSize: '0.5rem',
          height: 14,
          color: cfg.color,
          backgroundColor: cfg.bg,
          border: `1px solid ${cfg.color}55`,
          '& .MuiChip-label': { px: 0.4, fontWeight: 600, letterSpacing: 0.3 },
        }}
      />
    </Tooltip>
  );
}
